// Third-party packages
import {
	Destination,
} from '@local_types/index';

// Internal libraries (@stonal-tech/*)
import {
	logger,
} from '@config/logger';

interface DataLayerEvent {
	event: string;
	[key: string]: any;
}

export class GoogleTagManagerDestination implements Destination {
	public name = 'GoogleTagManager';
	public priority = 20;
	private queue: DataLayerEvent[] = [];
	private checkInterval: number = 100; // Check every 100ms
	private maxRetries: number = 100; // Stop after 10 seconds
	private retryCount: number = 0;
	private intervalId: number | null = null;
	private isInitialized: boolean = false;

	constructor() {
		this.initialize();
	}

	private initialize(): void {
		this.startChecking();
	}

	private startChecking(): void {
		if (this.intervalId) return;

		// Check immediately first
		if (this.isDataLayerReady()) {
			this.flushQueue();
			this.isInitialized = true;
			return;
		}

		// Set up interval checking
		this.intervalId = window.setInterval(() => {
			if (this.isDataLayerReady()) {
				this.flushQueue();
				this.stopChecking();
				this.isInitialized = true;
				logger.info('dataLayer found after retries', {
					retries: this.retryCount,
				});
			} else {
				this.retryCount++;
				if (this.retryCount >= this.maxRetries) {
					logger.warn('dataLayer not found after maximum retries');
					this.stopChecking();
				}
			}
		}, this.checkInterval);
	}

	private stopChecking(): void {
		if (this.intervalId) {
			clearInterval(this.intervalId);
			this.intervalId = null;
		}
	}

	private isDataLayerReady(): boolean {
		return !!(window.dataLayer && Array.isArray(window.dataLayer));
	}

	private flushQueue(): void {
		if (!this.isDataLayerReady()) return;

		while (this.queue.length > 0) {
			const event = this.queue.shift();
			if (event) {
				window.dataLayer!.push(event);
				logger.debug('GTM: Pushed queued event to dataLayer', event);
			}
		}
	}

	private push(event: DataLayerEvent): void {
		if (this.isDataLayerReady()) {
			window.dataLayer!.push(event);
			logger.debug('GTM: Pushed event to dataLayer', event);
		} else {
			this.queue.push(event);
			logger.debug('GTM: Queued event for dataLayer', event);

			// Restart checking if we stopped due to max retries
			if (!this.intervalId && !this.isInitialized) {
				this.retryCount = 0;
				this.startChecking();
			}
		}
	}

	page(name: string, properties: Record<string, any>): void {
		try {
			const gtmEvent: DataLayerEvent = {
				event: 'page_view',
				page_location: window.location.href,
				page_name: name,
				page_path: window.location.pathname,
				page_title: document.title,
				...properties,
			};

			this.push(gtmEvent);
			logger.info('GTM: Page view tracked', {
				name,
				properties,
			});
		} catch (error) {
			logger.error('GTM page tracking error:', error as Error);
		}
	}

	identify(userId: string | undefined, traits: Record<string, any>): void {
		if (!userId) {
			return;
		}

		try {
			const gtmEvent: DataLayerEvent = {
				event: 'user_identify',
				user_id: userId,
				user_properties: traits,
				...traits,
			};

			this.push(gtmEvent);
			logger.info('GTM: User identified', {
				traits,
				userId,
			});
		} catch (error) {
			logger.error('GTM identify error:', error as Error);
		}
	}

	track(event: string, properties: Record<string, any>): void {
		try {
			// Convert event name to GTM-friendly format (optional)
			// Example: "Button Clicked" -> "button_clicked"
			const gtmEventName = event.toLowerCase().replace(/\s+/g, '_');

			const gtmEvent: DataLayerEvent = {
				event: gtmEventName,
				event_category: 'custom',
				event_label: event,
				event_properties: properties,
				...properties,
			};

			this.push(gtmEvent);
		} catch (error) {
			logger.error('GTM track error:', error as Error);
		}
	}

	error(event: string, properties: Record<string, any>): void {
		try {
			const gtmEvent: DataLayerEvent = {
				error_message: event,
				error_properties: properties,
				event: 'error',
				...properties,
			};

			this.push(gtmEvent);
			logger.error('GTM: Error tracked', {
				event,
				properties,
			});
		} catch (error) {
			logger.error('GTM error tracking error:', error as Error);
		}
	}
}
